<template>
  <article class="px-4 pb-16">
    <div class="d-flex align-center mb-4">
      <v-btn
        color="primary"
        class="px-3 text-capitalize"
        depressed
        @click="$router.push({ name: 'website.products.gemPages' })"
      >
        <v-icon small>mdi-arrow-left mr-1</v-icon>
        Gem Pages
      </v-btn>
    </div>
    <h1 class="my-4">Create Gem Page</h1>
    <v-form ref="catalogForm">
      <!-- Gem page mode: When isGemMode is true we show a Gem Page creation UI in-place -->
      <template>
        <v-row>
          <v-col cols="12" md="8">
            <v-card class="pa-4 mb-4" outlined>
              <h2 class="mb-4">Gem Page</h2>
              <label class="form-label">Title *</label>
              <v-text-field
                placeholder="Title"
                outlined
                dense
                v-model="form.title"
                :error="validationErrors.title"
                :error-messages="validationErrors.title ? 'Title is required' : ''"
              />
              <!-- <v-text-field class="mt-4" label="Handle" outlined dense v-model="catalogPage.handle" /> -->
              <Description
                :title="'Body HTML'"
                :model="'bodyHtml'"
                :catalog="form"
                :catalogCreation="true"
                @update:bodyHtmlDescription="html => (form.bodyHtml = html)"
              />

              <!-- <v-text-field label="Search Engine Title" outlined dense v-model="catalogPage.searchEngineTitle" /> -->
              <!-- <v-text-field label="Search Engine Meta Description" outlined dense v-model="catalogPage.searchEngineMetaDescription" /> -->
              <!-- <v-select :items="['default','template1','template2']" label="Template" v-model="catalogPage.template" outlined dense /> -->
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <!-- Editable preview card for Gem Page (matches provided screenshot layout) -->
            <v-card class="pa-4" outlined>
              <!-- Preview box -->
              <div class="gem-preview" style="border-radius:8px; border:1px solid #eee; padding:12px; background:#fff;">
                <!-- (Header/countdown removed as requested) -->

                <!-- Image -->
                <div v-if="form.image" style="text-align:center; margin:10px 0;">
                  <img
                    :src="formatImage(form.image) || ''"
                    alt="gem image"
                    style="max-width:100%; border-radius:4px;"
                  />
                </div>

                <!-- Short description (HTML) rendered here -->
                <div
                  class="gem-short-description"
                  v-html="shortDescriptionPreviewHtml"
                  style="margin:8px 0; color:#444; padding: 8px;"
                ></div>

                <!-- CTA -->
                <div style="text-align:center; margin-top:8px;">
                  <v-btn
                    class="gem-cta-btn"
                    large
                    :style="ctaButtonStyle"
                    @click.prevent="form.linkProduct && window.open(form.linkProduct, '_blank')"
                  >
                    {{ form.ctaText || 'CHECK AVAILABILITY' }}
                  </v-btn>
                </div>

                <!-- Product Selection for Check Availability -->
                <div style="text-align:center; margin-top:8px;">
                  <v-btn
                    small
                    outlined
                    :color="validationErrors.product ? 'error' : 'primary'"
                    @click="configDialogSelection({ type: 'products', model: 'selectedProduct' })"
                  >
                    Select Product for Availability *
                  </v-btn>
                  <div v-if="form.selectedProduct" style="font-size:12px; color:#555; margin-top:4px;">
                    Product selected: {{ form.selectedProduct.title || form.selectedProduct.name }}
                  </div>
                  <div v-if="validationErrors.product" style="font-size:12px; color:#f44336; margin-top:4px;">
                    Please select a product
                  </div>
                </div>

                <!-- Guarantee -->
                <div style="text-align:center; margin-top:8px; font-size:12px; color:#777">
                  {{ form.guaranteeText || '30-day money back guarantee' }}
                </div>
              </div>

              <div class="d-flex justify-end my-2">
                <v-btn small outlined class="text-capitalize" @click="openCtaEditor">
                  <v-icon small class="mr-1">mdi-pencil</v-icon>
                  Edit CTA & Guarantee
                </v-btn>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- Editable controls -->
              <div class="d-flex" style="gap:8px; align-items:baseline; margin-bottom:8px;">
                <v-text-field label="Image URL" outlined dense v-model="form.image" @change="onGemImageChange" />
                <input
                  ref="gemUploadRef"
                  v-show="false"
                  accept="image/gif, image/jpeg, image/png , image/jpg, image/webp"
                  type="file"
                  @change="onGemUpload"
                />
                <!-- show loading state on upload button and disable while uploading -->
                <v-btn small text @click="onClickGemUpload" :loading="isGemUploading" :disabled="isGemUploading">
                  <v-icon small>mdi-upload</v-icon>
                  <span style="margin-left:6px">{{ isGemUploading ? 'Uploading...' : 'Upload' }}</span>
                </v-btn>
              </div>

              <div class="mb-2">
                <!-- Use the existing HurryBox editor to edit shortDescription HTML -->
                <HurryBox
                  :model="'shortDescription'"
                  :catalog="form"
                  :catalogCreation="true"
                  @update:bodyHtmlHurryBox="html => (form.shortDescription = html)"
                />
              </div>

              <!-- <v-text-field label="CTA Text" outlined dense v-model="form.ctaText" /> -->
              <!-- <v-text-field label="CTA Link (uses Link Product)" outlined dense v-model="form.linkProduct" /> -->
              <!-- <v-text-field label="Guarantee Text" outlined dense v-model="form.guaranteeText" /> -->

              <!-- <v-divider class="my-3"></v-divider> -->
              <!-- <v-switch label="Show Title" v-model="catalogPage.isShowTitle" /> -->
              <!-- <v-switch label="Published" v-model="catalogPage.published" /> -->
            </v-card>

            <v-card class="mt-4 pa-4" outlined>
              <h2 class="mb-4">Footer config</h2>
              <v-row>
                <v-col cols="12">
                  <v-switch v-model="form.showFooter" label="Show Footer" inset />
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="mb-1">Footer Background Color</div>
                  <v-menu v-model="footerEditor.bgMenu" offset-y :close-on-content-click="false">
                    <template #activator="{ on, attrs }">
                      <v-text-field outlined dense readonly v-bind="attrs" v-on="on" v-model="form.footerBgColor">
                        <template #prepend-inner>
                          <span
                            :style="{
                              display: 'inline-block',
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              background: form.footerBgColor,
                              border: '1px solid #ccc',
                              marginTop: '2px',
                            }"
                          ></span>
                        </template>
                      </v-text-field>
                    </template>
                    <v-color-picker v-model="form.footerBgColor" mode="hexa" show-swatches flat />
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="mb-1">Footer Text Color</div>
                  <v-menu v-model="footerEditor.colorMenu" offset-y :close-on-content-click="false">
                    <template #activator="{ on, attrs }">
                      <v-text-field outlined dense readonly v-bind="attrs" v-on="on" v-model="form.footerColor">
                        <template #prepend-inner>
                          <span
                            :style="{
                              display: 'inline-block',
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              background: form.footerColor,
                              border: '1px solid #ccc',
                              marginTop: '2px',
                            }"
                          ></span>
                        </template>
                      </v-text-field>
                    </template>
                    <v-color-picker v-model="form.footerColor" mode="hexa" show-swatches flat />
                  </v-menu>
                </v-col>
              </v-row>

              <div class="mb-3">
                <div class="mb-1">Footer Logo</div>
                <UploadOneImage :image="{ src: form.footerLogo }" @callback="onFooterLogoChange" />
              </div>

              <div class="mb-2">
                <div class="mb-1">Footer Copyright</div>
                <v-text-field
                  outlined
                  dense
                  placeholder="Copyright © 2026 All Rights Reserved."
                  v-model="form.footerCopyright"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-form>
    <v-divider class="my-6"></v-divider>
    <div class="d-flex justify-end">
      <v-btn outlined class="text-capitalize mr-3" text @click="onDiscard">Discard</v-btn>
      <v-btn color="primary" class="text-capitalize" :loading="isLoading" depressed @click="saveGemPage">Save</v-btn>
    </div>

    <!-- Product Selection Dialog -->
    <v-dialog :scrollable="false" v-model="dialog" width="900" content-class="l-dialog">
      <SelectionItems
        :model="configSelectionItem.model"
        :type="configSelectionItem.type"
        @returnData="getDataSelected"
        :dialog.sync="dialog"
        :dataSelected="configSelectionItem.data"
        :isSuperShow="true"
      />
    </v-dialog>

    <!-- CTA/Guarantee editor dialog -->
    <v-dialog v-model="ctaEditorDialog" max-width="560">
      <v-card>
        <v-card-title>Edit CTA & Guarantee</v-card-title>
        <v-card-text>
          <div class="mb-1">
            <div class="mb-1">CTA Text</div>
            <v-text-field outlined dense v-model="ctaEditor.ctaText" />
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <div class="mb-1">CTA Background Color</div>
              <v-menu v-model="ctaEditor.bgMenu" offset-y :close-on-content-click="false">
                <template #activator="{ on, attrs }">
                  <v-text-field outlined dense readonly v-bind="attrs" v-on="on" v-model="ctaEditor.ctaBgColor">
                    <template #prepend-inner>
                      <span
                        :style="{
                          display: 'inline-block',
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          background: ctaEditor.ctaBgColor,
                          border: '1px solid #ccc',
                          marginTop: '2px',
                        }"
                      ></span>
                    </template>
                  </v-text-field>
                </template>
                <v-color-picker v-model="ctaEditor.ctaBgColor" mode="hexa" show-swatches flat />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="mb-1">CTA Text Color</div>
              <v-menu v-model="ctaEditor.colorMenu" offset-y :close-on-content-click="false">
                <template #activator="{ on, attrs }">
                  <v-text-field outlined dense readonly v-bind="attrs" v-on="on" v-model="ctaEditor.ctaColor">
                    <template #prepend-inner>
                      <span
                        :style="{
                          display: 'inline-block',
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          background: ctaEditor.ctaColor,
                          border: '1px solid #ccc',
                          marginTop: '2px',
                        }"
                      ></span>
                    </template>
                  </v-text-field>
                </template>
                <v-color-picker v-model="ctaEditor.ctaColor" mode="hexa" show-swatches flat />
              </v-menu>
            </v-col>
          </v-row>

          <div>
            <div class="mb-1">Guarantee Text</div>
            <v-text-field outlined dense v-model="ctaEditor.guaranteeText" />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-capitalize" @click="ctaEditorDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed class="text-capitalize" @click="saveCtaEditor">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </article>
</template>

<script>
import axios from 'axios';
import imageHelpers from '@/helpers/image';
import { uploadService } from '@/apis/upload.s';
import UploadOneImage from '@/components/form/UploadOneImage.vue';
import SelectionItems from '../../apps/UpSell/component/SelectionItems';
import { catalogService } from '@/apis/catalog';
import '../components/description/styles/swiper.css';
import '../components/description/styles/section-style.scss';
import '../components/description/styles/section-style-backup.scss';
import '../components/description/styles/section-small.scss';
import '../components/description/styles/description-style.scss';
import { processFinalCatalogHtml } from '../components/description/common/utils';
import Description from '../components/description/index.vue';
import HurryBox from '../components/description/hurry-box.vue';

export default {
  components: {
    UploadOneImage,
    Description,
    HurryBox,
    SelectionItems,
  },
  computed: {
    shortDescriptionPreviewHtml() {
      return this.remapSectionDomIdsForPreview(this.form.shortDescription);
    },
    ctaButtonStyle() {
      return {
        backgroundColor: this.form.ctaBgColor || '#5e8e49',
        color: this.form.ctaColor || '#ffffff',
      };
    },
  },
  data() {
    return {
      form: {
        bodyHtml: '',
        handle: '',
        isShowTitle: true,
        published: true,
        title: '',
        shortDescription: '',
        image: '',
        linkProduct: '',
        searchEngineMetaDescription: '',
        searchEngineTitle: '',
        template: 'page',
        ctaText: 'CHECK AVAILABILITY',
        ctaBgColor: '#5e8e49',
        ctaColor: '#ffffff',
        guaranteeText: '30-day money back guarantee',
        showFooter: true,
        footerBgColor: '#111111',
        footerColor: '#ffffff',
        footerLogo: '',
        footerCopyright: 'Copyright © 2026 All Rights Reserved.',
        product: '',
        selectedProduct: null, // Single product for check availability
      },
      loading: false,
      isLoading: false,
      imageHelpers,
      isGemUploading: false,
      // Validation errors
      validationErrors: {
        title: false,
        product: false,
      },
      // Product selection dialog properties
      dialog: false,
      configSelectionItem: {
        type: 'products',
        model: 'selectedProduct',
        data: [],
      },
      ctaEditorDialog: false,
      ctaEditor: {
        ctaText: '',
        ctaBgColor: '#5e8e49',
        ctaColor: '#ffffff',
        guaranteeText: '',
        bgMenu: false,
        colorMenu: false,
      },
      footerEditor: {
        bgMenu: false,
        colorMenu: false,
      },
    };
  },
  methods: {
    remapSectionDomIdsForPreview(html) {
      if (!html || typeof html !== 'string') return html || '';

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
        const root = doc.body.firstElementChild;
        if (!root) return html;

        const idMap = new Map();
        const randomKey = () =>
          Math.random()
            .toString(36)
            .slice(2, 8);

        const remapPrl = value => {
          if (!value || typeof value !== 'string') return value;
          return value.replace(/prl-[a-z0-9]{6}/g, match => {
            if (!idMap.has(match)) idMap.set(match, `pv-${randomKey()}`);
            return idMap.get(match);
          });
        };

        const remapDataSection = value => {
          if (!value || typeof value !== 'string') return value;
          if (!/^[a-z0-9-]+$/i.test(value)) return value;
          if (!/-[a-z0-9]{6}$/i.test(value)) return value;
          if (!idMap.has(value)) idMap.set(value, value.replace(/-[a-z0-9]{6}$/i, `-${randomKey()}`));
          return idMap.get(value);
        };

        const walk = el => {
          if (!el || el.nodeType !== 1) return;

          if (el.hasAttribute('id')) el.setAttribute('id', remapPrl(el.getAttribute('id')));
          if (el.hasAttribute('data-id')) el.setAttribute('data-id', remapPrl(el.getAttribute('data-id')));
          if (el.hasAttribute('data-section'))
            el.setAttribute('data-section', remapDataSection(el.getAttribute('data-section')));

          // Common ref attrs that may point to ids
          if (el.hasAttribute('for')) el.setAttribute('for', remapPrl(el.getAttribute('for')));
          if (el.hasAttribute('href')) {
            const href = el.getAttribute('href');
            if (href && href.startsWith('#')) el.setAttribute('href', `#${remapPrl(href.slice(1))}`);
          }

          for (const child of Array.from(el.children)) walk(child);
        };

        walk(root);
        return root.innerHTML;
      } catch (_e) {
        return html;
      }
    },
    onDiscard() {
      this.$router.push({ name: 'website.products.gemPages' });
    },
    onClickGemUpload() {
      // Prevent clicking while an upload is in progress
      if (this.isGemUploading) return;
      this.$refs.gemUploadRef.click();
    },
    onGemImageChange() {
      // when image URL is changed, reflect it into images array for preview/use
      // if (this.catalog.catalogPage.image) {
      //   this.catalog.catalogPage.images = [{ src: this.catalog.catalogPage.image, index: 0 }]
      // }
    },
    onFooterLogoChange(image) {
      this.form.footerLogo = image?.src || '';
    },
    generateObjectID() {
      const hex = value => Math.floor(value).toString(16);
      return hex(Date.now() / 1000) + ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16));
    },
    formatImage(item) {
      return imageHelpers.url(item, { width: 320, height: 320 });
    },
    openCtaEditor() {
      this.ctaEditor = {
        ctaText: this.form.ctaText || 'CHECK AVAILABILITY',
        ctaBgColor: this.form.ctaBgColor || '#5e8e49',
        ctaColor: this.form.ctaColor || '#ffffff',
        guaranteeText: this.form.guaranteeText || '30-day money back guarantee',
        bgMenu: false,
        colorMenu: false,
      };
      this.ctaEditorDialog = true;
    },
    saveCtaEditor() {
      this.form.ctaText = this.ctaEditor.ctaText || 'CHECK AVAILABILITY';
      this.form.ctaBgColor = this.ctaEditor.ctaBgColor || '#5e8e49';
      this.form.ctaColor = this.ctaEditor.ctaColor || '#ffffff';
      this.form.guaranteeText = this.ctaEditor.guaranteeText || '30-day money back guarantee';
      this.ctaEditorDialog = false;
    },
    // Product selection methods
    configDialogSelection(data) {
      this.dialog = true;
      this.configSelectionItem = Object.assign({}, data);
      if (this.form.selectedProduct && data.model === 'selectedProduct') {
        this.configSelectionItem.data = this.form.selectedProduct ? [this.form.selectedProduct] : [];
      }
    },
    getDataSelected(data, model) {
      // For single product selection, take only the first product
      if (model === 'selectedProduct' && data && data.length > 0) {
        this.form[model] = data[0]; // Only take the first selected product
        // Save the product ID to the product field
        this.form.product = data[0]._id || data[0].id;
        // Clear validation error when product is selected
        this.validationErrors.product = false;
      } else if (model === 'selectedProduct') {
        this.form[model] = null;
        this.form.product = '';
      } else {
        this.form[model] = JSON.parse(JSON.stringify(data));
      }
    },
    async onGemUpload(e) {
      const { files } = e.target;
      if (files?.length) {
        // mark uploading so UI shows spinner / disables actions
        this.isGemUploading = true;
        try {
          const file = files[0];
          const type = file.name.split('.').pop();
          const id = this.generateObjectID();
          const { data } = await uploadService.get({ filename: `image-${id}.${type}` });
          if (data) {
            await axios.put(data.presignedUrl, file, {
              headers: {
                'Content-Type': `${file.type}`,
              },
            });
            const imagePath = data?.imagePath ?? '';
            this.form.image = imagePath;
          }
          // this.form.images = [{ src: imagePath, index: 0 }]
        } catch (error) {
          console.log('🚀 ~ file: index.vue ~ onGemUpload ~ error:', error);
        } finally {
          // always clear uploading flag
          this.isGemUploading = false;
        }
      }
    },
    async saveGemPage() {
      // Reset validation errors
      this.validationErrors.title = false;
      this.validationErrors.product = false;

      let hasErrors = false;

      // Validation: Check if title is required
      if (!this.form.title || !this.form.title.trim()) {
        this.validationErrors.title = true;
        hasErrors = true;
      }

      // Validation: Check if product is required
      if (!this.form.product) {
        this.validationErrors.product = true;
        hasErrors = true;
      }

      if (hasErrors) {
        return;
      }

      this.loading = true;
      try {
        delete this.form.selectedProduct; // Remove preview-only field
        const payload = {
          catalogPage: {
            ...this.form,
            bodyHtml: processFinalCatalogHtml(this.form.bodyHtml),
            shortDescription: processFinalCatalogHtml(this.form.shortDescription),
            searchEngineTitle: this.form.title,
            searchEngineMetaDescription: this.form.shortDescription,
          },
        };
        await catalogService.postGemPage(payload);
        this.$router.push({ name: 'website.products.gemPages' });
      } catch (e) {
        alert('Error saving Gem Page');
      }
      this.loading = false;
    },
  },
  watch: {
    'form.title': function(newVal) {
      // Clear title validation error when user starts typing
      if (newVal && newVal.trim() && this.validationErrors.title) {
        this.validationErrors.title = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-label {
  font-weight: 500;
  font-size: 14px;
  opacity: 0.8;
}
.sections-panel {
  position: sticky;
  top: 64px;
  z-index: 1;
  .title-section-description {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .sections-description {
    padding: 5px 10px 20px;
    .section-description-item {
      font-size: 14px;
      cursor: pointer;
      margin-bottom: 7px;
    }
  }
  .catalog-section-item {
    cursor: pointer;
  }
}
.transition-transform {
  transition: transform 0.3s ease;
}

.rotate-icon {
  transform: rotate(180deg);
}
/* Nâng cấp giao diện Gem Page Create */
.gem-page-create {
  margin: 32px 24px;
  display: flex;
  gap: 32px;
}
.enhanced-bg {
  background: linear-gradient(135deg, #f8fafc 60%, #e3e7ed 100%);
  border-radius: 16px;
  box-shadow: 0 4px 24px #0001;
}
.enhanced-card {
  border-radius: 16px;
  box-shadow: 0 2px 16px #0002;
  background: #fff;
}
.enhanced-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 8px;
  transition: box-shadow 0.2s, background 0.2s;
}
.enhanced-btn:hover {
  box-shadow: 0 2px 8px #1976d21a;
  background: #e3e7ed !important;
}

/* Gem CTA button plain CSS replacement for Tailwind utilities */
.gem-cta-btn {
  /* sizing and spacing */
  display: inline-block;
  width: 100%;
  text-align: center;
  padding: 0.75rem 1rem; /* py-3 px-4 */
  margin-bottom: 0.5rem; /* mb-2 */

  /* appearance */
  border-radius: 0.375rem; /* rounded-md */
  font-weight: 600; /* font-semibold */

  /* no hover color override so custom colors stay consistent */
  transition: box-shadow 0.2s ease;
}
</style>
